import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { TELEGRAM_CONFIG } from './config';
import './App.css';

const PICKUP_LOCATIONS = [
  'Gare de Lyon',
  'Gare de Montparnasse',
  'Gare du Nord',
  "Gare de l'Est",
  'Aéroport Charles de Gaulle',
  'Aéroport Orly'
];

function App() {
  const [formData, setFormData] = useState({
    pickupLocation: '',
    destinationLocation: '',
    firstName: '',
    lastName: '',
    phoneNumber: ''
  });

  const [pickupChoice, setPickupChoice] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [isSendingToTelegram, setIsSendingToTelegram] = useState(false);
  const [reservationStatus, setReservationStatus] = useState('idle'); // idle | sending | sent | error
  const [tripTimes, setTripTimes] = useState(null);
  const ticketRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getCurrentLocation = (showAlert = true) => {
    if (!navigator.geolocation) {
      if (showAlert) {
        alert('La géolocalisation n\'est pas supportée par votre navigateur.');
      }
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Utilisation de l'API de géocodage inverse pour obtenir l'adresse
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=fr`
          );
          
          if (response.ok) {
            const data = await response.json();
            const address = `${data.locality || ''} ${data.city || ''} ${data.principalSubdivision || ''}`.trim();
            
            setFormData(prev => ({
              ...prev,
              pickupLocation: address || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
            }));
          } else {
            // Fallback avec les coordonnées si l'API échoue
            setFormData(prev => ({
              ...prev,
              pickupLocation: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
            }));
          }
        } catch (error) {
          console.error('Erreur lors de la géolocalisation:', error);
          if (showAlert) {
            alert('Erreur lors de la récupération de l\'adresse. Veuillez choisir un lieu dans la liste.');
          }
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        setIsLocating(false);
        if (showAlert) {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              alert('Permission de géolocalisation refusée. Veuillez choisir un lieu dans la liste.');
              break;
            case error.POSITION_UNAVAILABLE:
              alert('Position indisponible. Veuillez choisir un lieu dans la liste.');
              break;
            case error.TIMEOUT:
              alert('Délai d\'attente dépassé. Veuillez réessayer ou choisir un lieu.');
              break;
            default:
              alert('Erreur de géolocalisation. Veuillez choisir un lieu dans la liste.');
              break;
          }
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const handlePickupChoice = (e) => {
    const value = e.target.value;
    setPickupChoice(value);

    if (value === '__gps__') {
      setFormData(prev => ({ ...prev, pickupLocation: '' }));
      getCurrentLocation(true);
    } else {
      setFormData(prev => ({ ...prev, pickupLocation: value }));
    }
  };

  const generateTicketImage = async () => {
    if (ticketRef.current) {
      try {
        const canvas = await html2canvas(ticketRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        });

        return canvas.toDataURL();
      } catch (error) {
        console.error('Erreur lors de la génération de l\'image:', error);
        return null;
      }
    }
    return null;
  };

  const sendToTelegram = async (imageDataUrl) => {
    setIsSendingToTelegram(true);
    
    try {
      // Vérifier la configuration
      if (TELEGRAM_CONFIG.BOT_TOKEN === 'YOUR_BOT_TOKEN_HERE' || TELEGRAM_CONFIG.CHAT_ID === 'YOUR_CHAT_ID_HERE') {
        console.log('Configuration Telegram manquante');
        return false;
      }
      
      // Convertir data URL en blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      
      // Créer FormData pour l'envoi à Telegram
      const telegramFormData = new FormData();
      telegramFormData.append('chat_id', TELEGRAM_CONFIG.CHAT_ID);
      telegramFormData.append('photo', blob, `bon-reservation-${formData.firstName}-${formData.lastName}.png`);
      telegramFormData.append('caption', `🏍️ NSA SERVICES - Nouvelle réservation\n\n👤 Client: ${formData.firstName} ${formData.lastName}\n📞 Téléphone: ${formData.phoneNumber}\n📅 Date: ${new Date().toLocaleDateString('fr-FR')}\n🕐 Heure: ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}\n📍 Prise en charge: ${formData.pickupLocation}\n🎯 Destination: ${formData.destinationLocation}\n\n📋 Bon de réservation généré automatiquement`);
      
      // Envoyer à Telegram
      const telegramResponse = await fetch(`https://api.telegram.org/bot${TELEGRAM_CONFIG.BOT_TOKEN}/sendPhoto`, {
        method: 'POST',
        body: telegramFormData
      });
      
      if (telegramResponse.ok) {
        console.log('Bon de réservation envoyé à NSA SERVICES avec succès');
        return true;
      } else {
        const errorData = await telegramResponse.json();
        console.error('Erreur Telegram:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi à Telegram:', error);
      return false;
    } finally {
      setIsSendingToTelegram(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des champs requis
    if (!formData.pickupLocation || !formData.destinationLocation || !formData.firstName || !formData.lastName || !formData.phoneNumber) {
      if (pickupChoice === '__gps__' && !formData.pickupLocation) {
        alert('Veuillez attendre la localisation GPS ou choisir un autre lieu.');
      } else {
        alert('Veuillez remplir tous les champs obligatoires.');
      }
      return;
    }

    // Démarrer l'envoi (ne pas afficher ni télécharger le ticket)
    const pickupAt = new Date();
    const reservedAt = new Date(pickupAt.getTime() - 20 * 60 * 1000);
    setTripTimes({ pickupAt, reservedAt });
    setReservationStatus('sending');

    // Attendre un peu pour que le DOM se mette à jour
    setTimeout(async () => {
      const imageDataUrl = await generateTicketImage();
      if (imageDataUrl) {
        // Envoi automatique à NAS SERVICES via Telegram
        const ok = await sendToTelegram(imageDataUrl);
        setReservationStatus(ok ? 'sent' : 'error');
      } else {
        setReservationStatus('error');
      }
    }, 500);
  };

  const pickupAt = tripTimes?.pickupAt || new Date();
  const reservedAt = tripTimes?.reservedAt || new Date(pickupAt.getTime() - 20 * 60 * 1000);
  const formatDateTime = (date) =>
    `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div 
      className="app"
      style={{
        backgroundImage: `url('/paris-pattern.png')`,
        backgroundRepeat: 'repeat',
        backgroundSize: '320px',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Images superposées */}
      <div className="honda-image-container">
        <img 
          src="/Honda-5-1024x628.webp" 
          alt="Paris - NSA SERVICES" 
          className="honda-image"
        />
        <img 
          src="/honda-goldwing.png" 
          alt="Honda Goldwing - NSA SERVICES" 
          className="moto-image"
        />
      </div>
      
      <div className="container">
        {/* Logo */}
        <div className="logo-container">
          <div className="logo">
            🏍️ NSA SERVICES
          </div>
          <p className="tagline">Transport rapide et efficace</p>
        </div>

        {/* Formulaire de réservation - affiché seulement si pas de réservation en cours */}
        {reservationStatus === 'idle' && (
          <form className="reservation-form" onSubmit={handleSubmit}>
            <h2>Réservation</h2>
            
            <div className="form-group">
              <label htmlFor="pickupLocation">Lieu de prise en charge *</label>
              <select
                id="pickupLocation"
                name="pickupLocation"
                value={pickupChoice}
                onChange={handlePickupChoice}
                required
              >
                <option value="" disabled>
                  Choisir un lieu ou la GPS
                </option>
                {PICKUP_LOCATIONS.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
                <option value="__gps__">📍 Ma position GPS</option>
              </select>
              {pickupChoice === '__gps__' && (
                <p className="gps-address">
                  {isLocating
                    ? 'Localisation en cours...'
                    : formData.pickupLocation || 'Position non disponible'}
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="destinationLocation">Lieu de destination *</label>
              <input
                type="text"
                id="destinationLocation"
                name="destinationLocation"
                value={formData.destinationLocation}
                onChange={handleInputChange}
                placeholder="Adresse de destination"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">Prénom *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Votre prénom"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Nom *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Votre nom"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Numéro de téléphone *</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="06 12 34 56 78"
                required
              />
            </div>

            <button type="submit" className="submit-btn">
              Réserver maintenant
            </button>
          </form>
        )}

        {/* Ticket rendu hors écran pour génération d'image (non affiché à l'utilisateur) */}
        <div style={{ position: 'absolute', left: '-10000px', top: 0 }} aria-hidden="true">
          <div className="ticket" ref={ticketRef}>
            <div className="ticket-header">
              <h2>SERVICE DE TRANSPORT VMDTR</h2>
              <h3>BILLET INDIVIDUEL</h3>
              <p>Conformément à (l'Arreté du 14 Février 1986 - Art. 5)</p>
            </div>
            
            <div className="ticket-section">
              <h4>ORDRE DE MISSION</h4>
              <p>(Article R3120-2 du code des transports - Arrêté du 30 juillet 2013)-(Arreté du 6 Janvier 1993)</p>
              <p>(Code de la route, art. R431-1 et suivants)</p>
            </div>

            <div className="ticket-info">
              <div className="info-row">
                <span><strong>Nom de société :</strong></span>
                <span>NSA SERVICES (SAS)</span>
              </div>
              <div className="info-row">
                <span><strong>SIREN :</strong></span>
                <span>891 463 788</span>
              </div>
              <div className="info-row">
                <span><strong>SIRET :</strong></span>
                <span>891 463 788 00010</span>
              </div>
              <div className="info-row">
                <span><strong>N° TVA :</strong></span>
                <span>FR11 891 463 788</span>
              </div>
              <div className="info-row">
                <span><strong>Adresse :</strong></span>
                <span>52 Rue Villebois Mareuil, 92230 Gennevilliers</span>
              </div>
              <div className="info-row">
                <span><strong>Conducteur :</strong></span>
                <span>Goldwing 1800 - EC268HE</span>
              </div>
            </div>

            <div className="passenger-info">
              <h4>Passager : {formData.firstName} {formData.lastName}</h4>
              <p>{formData.phoneNumber}</p>
            </div>

            <div className="trip-details">
              <div className="detail-row">
                <span><strong>• Date de réservation :</strong></span>
                <span>{formatDateTime(reservedAt)}</span>
              </div>
              <div className="detail-row">
                <span><strong>• Prise en charge :</strong></span>
                <span>{formatDateTime(pickupAt)}</span>
              </div>
              <div className="detail-row">
                <span><strong>• Prise en charge :</strong></span>
                <span>{formData.pickupLocation}</span>
              </div>
              <div className="detail-row">
                <span><strong>• Destination :</strong></span>
                <span>{formData.destinationLocation}</span>
              </div>
              <div className="detail-row">
                <span><strong>• Tarification :</strong></span>
                <span>à partir de 70€ ht ( à définir )</span>
              </div>
            </div>

            <div className="ticket-footer">
              <p>Je confirme ma réservation pour le transport mentionné ci-dessus et j'accepte les termes et conditions liés à ce transport à titre onéreux.</p>
              <p><strong>Ce bon fait foi de mon droit légal pour ce transport.</strong></p>
            </div>
          </div>
        </div>

        {/* Confirmation de réservation avec design amélioré */}
        {reservationStatus === 'sending' && (
          <div className="confirmation-card">
            <div className="confirmation-icon sending">⏳</div>
            <h2>Envoi en cours...</h2>
            <p>Votre réservation est en cours d'envoi</p>
          </div>
        )}
        {reservationStatus === 'sent' && (
          <div className="confirmation-card">
            <div className="confirmation-icon success">✓</div>
            <h2>Réservation confirmée !</h2>
            <p>Votre réservation a été envoyée avec succès à NSA SERVICES.</p>
            <p className="confirmation-details">
              <strong>{formData.firstName} {formData.lastName}</strong><br />
              {formData.phoneNumber}<br />
              📍 {formData.pickupLocation}<br />
              🎯 {formData.destinationLocation}
            </p>
          </div>
        )}
        {reservationStatus === 'error' && (
          <div className="confirmation-card">
            <div className="confirmation-icon error">✕</div>
            <h2>Erreur d'envoi</h2>
            <p>Un problème est survenu lors de l'envoi de votre réservation.</p>
            <button 
              className="retry-btn" 
              onClick={() => setReservationStatus('idle')}
            >
              Réessayer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
